import { apiBaseUrl } from "../../client/src/main.ts";
import { toast } from "react-hot-toast";
import { endpoints } from "./docsData.ts";
import { CopyIcon, DownloadIcon } from "./icons.tsx";
import { Highlight, type RenderProps, themes } from "prism-react-renderer";
import { useEffect, useState } from "preact/hooks";

const codeBlockStyle = {
  padding: "16px",
  borderRadius: "8px",
  overflowX: "auto",
  fontSize: "13px",
  lineHeight: "1.6",
  margin: 0,
  maxWidth: "100%",
  boxSizing: "border-box",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
} as const;

const copyButtonStyle = {
  position: "absolute",
  top: "8px",
  right: "8px",
  padding: "6px 12px",
  backgroundColor: "#2d2d2d",
  color: "#d4d4d4",
  border: "1px solid #4d4d4d",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "12px",
  fontFamily: "sans-serif",
  transition: "background-color 0.2s",
} as const;

const codeBlockContainerStyle = {
  position: "relative",
  marginBottom: "8px",
};

const sectionStyle = {
  marginBottom: "32px",
  padding: "clamp(12px, 3vw, 20px)",
  backgroundColor: "var(--surface-alt)",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  overflowX: "hidden" as const,
};

const headerStyle = {
  marginTop: 0,
  marginBottom: "8px",
  color: "#4fc3f7",
};

const badgeStyle = (color: string) => ({
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 600,
  backgroundColor: color,
  color: "white",
  marginRight: "8px",
});

const endpointUrlStyle = {
  fontSize: "14px",
  wordBreak: "break-all",
  overflowWrap: "anywhere",
} as const;

const navLinkStyle = (isActive: boolean) => ({
  display: "block",
  padding: "6px 10px",
  borderRadius: "4px",
  color: isActive ? "#4fc3f7" : "var(--text)",
  backgroundColor: isActive ? "rgba(79, 195, 247, 0.12)" : "transparent",
  textDecoration: "none",
  fontSize: "13px",
  fontWeight: isActive ? 600 : 400,
  transition: "all 0.15s ease",
  wordBreak: "break-all" as const,
});

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success("Copied to clipboard!");
    },
    () => {
      toast.error("Failed to copy");
    },
  );
};

const n8nAutoResponderWorkflow = {
  name: "WhatsApp AI Auto-Responder with Supergreen",
  nodes: [
    {
      parameters: {
        content:
          "## WhatsApp Auto-Responder with Supergreen\n\n### How it works\n\nThis workflow listens for incoming WhatsApp messages via the Supergreen Trigger node and automatically sends an instant response back to the sender chat using Supergreen. Because Supergreen connects directly via WhatsApp Web sessions behind static proxies, you don't need Meta Business verification or approved message templates.\n\n### Setup steps\n\n- Configure your Supergreen API credentials in n8n (base URL and account secret token from supergreen.cc).\n- Select your connected phone number in the Supergreen Trigger node.\n- Customize the response message or connect an AI model in between the trigger and the reply node.\n- Activate the workflow. The webhook automatically registers with Supergreen to stream incoming messages in real time.\n\n### Customization\n\nInsert an AI Agent node (e.g. OpenAI or Anthropic) or a Code node before the reply step to generate dynamic, contextual answers for customer support or lead qualification.",
        width: 480,
        height: 752,
      },
      type: "n8n-nodes-base.stickyNote",
      typeVersion: 1,
      position: [-352, 144],
      id: "31b2c48e-7e9a-4c22-901d-5a8f0e381bd2",
      name: "Sticky Note",
    },
    {
      parameters: {
        content:
          "## Listen and Auto-Reply\n\nA compact two-node flow that triggers on incoming WhatsApp messages and sends an immediate response back using Supergreen.",
        width: 448,
        height: 320,
        color: 7,
      },
      type: "n8n-nodes-base.stickyNote",
      typeVersion: 1,
      position: [208, 144],
      id: "59a721ef-6b21-4f9e-8c33-1d4e680a9bc4",
      name: "Sticky Note1",
    },
    {
      id: "1",
      name: "When WhatsApp Message Received",
      type: "n8n-nodes-supergreen.supergreenTrigger",
      position: [250, 300],
      parameters: {
        network: "whatsapp",
        events: ["message"],
        onlyIncoming: true,
      },
      credentials: {
        supergreenApi: {
          id: "1",
          name: "Supergreen Account",
        },
      },
      typeVersion: 1,
    },
    {
      id: "2",
      name: "Send WhatsApp Reply",
      type: "n8n-nodes-supergreen.supergreen",
      position: [500, 300],
      parameters: {
        resource: "message",
        operation: "send",
        toNumber: "={{ $json.chat.id }}",
        message:
          '=Thanks for your message: "{{ $json.text }}". An automated reply from n8n + Supergreen.',
      },
      credentials: {
        supergreenApi: {
          id: "1",
          name: "Supergreen Account",
        },
      },
      typeVersion: 1,
    },
  ],
  active: false,
  settings: {
    executionOrder: "v1",
  },
  connections: {
    "When WhatsApp Message Received": {
      main: [
        [
          {
            node: "Send WhatsApp Reply",
            type: "main",
            index: 0,
          },
        ],
      ],
    },
  },
};

const n8nSendDocumentWorkflow = {
  name: "Send Document or PDF via WhatsApp with Supergreen",
  nodes: [
    {
      parameters: {
        content:
          "## Send Document or PDF via WhatsApp with Supergreen\n\n### How it works\n\nThis workflow receives an incoming webhook request and uses the received data to send a document or PDF through WhatsApp using Supergreen. The webhook acts as the entry point, then passes execution directly to the Supergreen node that performs the message delivery.\n\n### Setup steps\n\n- Configure the Incoming Webhook URL in n8n and share it with the system that will trigger PDF or document sends.\n- Set up Supergreen credentials/API access in n8n for the Supergreen Send PDF node.\n- Configure the Supergreen node with the target WhatsApp recipient, document/PDF URL or binary field, filename, and any required message parameters from the webhook payload.\n- Test the webhook with a sample payload to confirm the document is delivered successfully.\n\n### Customization\n\nAdjust the webhook payload mapping in the Supergreen node to support different recipients, document URLs, filenames, captions, or message templates.",
        width: 480,
        height: 752,
      },
      type: "n8n-nodes-base.stickyNote",
      typeVersion: 1,
      position: [-352, 144],
      id: "4259102c-4bfe-43fc-a833-a28ed7059ce6",
      name: "Sticky Note",
    },
    {
      parameters: {
        content:
          "## Receive and send PDF\n\nA compact two-node flow that starts from an incoming webhook and immediately sends the requested PDF or document via WhatsApp using Supergreen.",
        width: 448,
        height: 320,
        color: 7,
      },
      type: "n8n-nodes-base.stickyNote",
      typeVersion: 1,
      position: [208, 144],
      id: "6262010a-b13e-46b4-a8d7-079ff7cc6c9f",
      name: "Sticky Note1",
    },
    {
      id: "1",
      name: "When Invoice Submitted",
      type: "n8n-nodes-base.webhook",
      position: [250, 300],
      parameters: {
        path: "send-invoice",
        httpMethod: "POST",
        responseMode: "onReceived",
      },
      typeVersion: 1,
    },
    {
      id: "2",
      name: "Send PDF via Supergreen WhatsApp",
      type: "n8n-nodes-supergreen.supergreen",
      position: [500, 300],
      parameters: {
        type: "document",
        mediaUrl: "={{ $json.body.fileUrl }}",
        resource: "media",
        toNumber: "={{ $json.body.recipientPhone }}",
        operation: "sendMedia",
        mediaSource: "url",
        additionalFields: {
          caption: "={{ $json.body.caption || 'Here is your document.' }}",
          filename: "={{ $json.body.filename || 'document.pdf' }}",
        },
      },
      credentials: {
        supergreenApi: {
          id: "1",
          name: "Supergreen Account",
        },
      },
      typeVersion: 1,
    },
  ],
  active: false,
  settings: {
    executionOrder: "v1",
  },
  connections: {
    "When Invoice Submitted": {
      main: [
        [
          {
            node: "Send PDF via Supergreen WhatsApp",
            type: "main",
            index: 0,
          },
        ],
      ],
    },
  },
};

const makeAutoResponderBlueprint = {
  name: "Supergreen - WhatsApp AI Auto-Responder",
  flow: [
    {
      id: 1,
      module: "gateway:CustomWebHook",
      version: 1,
      filter: {
        name: "Ignore Outgoing Messages",
        conditions: [
          [
            { a: "{{1.fromMe}}", o: "boolean:not:true" },
            { a: "{{1.event}}", o: "text:equal", b: "message" },
          ],
        ],
      },
    },
    {
      id: 2,
      module: "http:ActionMakeRequest",
      version: 3,
      mapper: {
        url: "https://api.supergreen.cc/",
        method: "POST",
        headers: [{ name: "Content-Type", value: "application/json" }],
        bodyType: "raw",
        parseResponse: true,
        data:
          '{\n  "endpoint": "sendMessage",\n  "payload": {\n    "token": "YOUR_SUPERGREEN_TOKEN",\n    "fromNumber": "YOUR_PHONE",\n    "toNumber": "{{1.chat.id}}",\n    "message": "Automated reply via Make.com"\n  }\n}',
      },
    },
  ],
};

const makeSendMediaBlueprint = {
  name: "Supergreen - Send WhatsApp Document",
  flow: [
    {
      id: 1,
      module: "gateway:CustomWebHook",
      version: 1,
    },
    {
      id: 2,
      module: "http:ActionMakeRequest",
      version: 3,
      mapper: {
        url: "https://api.supergreen.cc/",
        method: "POST",
        headers: [{ name: "Content-Type", value: "application/json" }],
        bodyType: "raw",
        parseResponse: true,
        data:
          '{\n  "endpoint": "sendMedia",\n  "payload": {\n    "token": "YOUR_SUPERGREEN_TOKEN",\n    "fromNumber": "YOUR_PHONE",\n    "toNumber": "{{1.recipientNumber}}",\n    "type": "document",\n    "base64": "{{1.fileBase64}}",\n    "filename": "{{1.filename}}"\n  }\n}',
      },
    },
  ],
};

const downloadJson = (filename: string, data: object) => {
  if (typeof document === "undefined") return;
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const CodeBlock = (
  { code, language = "json" }: { code: string; language?: string },
) => (
  <div style={codeBlockContainerStyle}>
    <button
      type="button"
      style={copyButtonStyle}
      onClick={() => copyToClipboard(code)}
      onMouseEnter={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#3d3d3d";
      }}
      onMouseLeave={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#2d2d2d";
      }}
    >
      Copy
    </button>
    <Highlight theme={themes.vsDark} code={code} language={language}>
      {({ style, tokens, getLineProps, getTokenProps }: RenderProps) => (
        <pre style={{ ...codeBlockStyle, ...style }}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  </div>
);

const EndpointSection = (
  {
    name,
    method,
    endpoint,
    description,
    auth,
    requestBody,
    responseBody,
    example,
  }: {
    name: string;
    method: "POST";
    endpoint: string;
    description: string;
    auth: string;
    requestBody: Record<
      string,
      { type: string; required?: boolean; description: string }
    >;
    responseBody: Record<string, { type: string; description: string }>;
    example: { request: unknown; response: unknown };
  },
) => (
  <div style={sectionStyle} id={name}>
    <h3 style={headerStyle}>{name}</h3>
    <div style={{ marginBottom: "16px" }}>
      <span style={badgeStyle("#2e7d32")}>{method}</span>
      <code style={endpointUrlStyle}>{apiBaseUrl}{endpoint}</code>
    </div>
    <p style={{ marginBottom: "16px", opacity: 0.9 }}>{description}</p>

    <div style={{ marginBottom: "16px" }}>
      <strong>Authentication:</strong> {auth}
    </div>

    {Object.keys(requestBody).length > 0 && (
      <div style={{ marginBottom: "16px" }}>
        <strong>Request Body:</strong>
        <div style={{ marginTop: "8px" }}>
          {Object.entries(requestBody).map(([key, value]) => (
            <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
              <code style={{ color: "#ce9178" }}>{key}</code>{" "}
              <span style={{ opacity: 0.7 }}>({value.type})</span>
              {value.required && (
                <span style={{ color: "#f48771" }}>*required</span>
              )}
              <div
                style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}
              >
                {value.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {Object.keys(responseBody).length > 0 && (
      <div style={{ marginBottom: "16px" }}>
        <strong>Response:</strong>
        <div style={{ marginTop: "8px" }}>
          {Object.entries(responseBody).map(([key, value]) => (
            <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
              <code style={{ color: "#ce9178" }}>{key}</code>{" "}
              <span style={{ opacity: 0.7 }}>({value.type})</span>
              <div
                style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}
              >
                {value.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    <div>
      <strong>Example:</strong>
      <div style={{ marginTop: "8px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Request:
        </div>
        <CodeBlock code={JSON.stringify(example.request, null, 2)} />
      </div>
      <div style={{ marginTop: "12px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Response:
        </div>
        <CodeBlock code={JSON.stringify(example.response, null, 2)} />
      </div>
    </div>
  </div>
);

export const ApiDocs = () => {
  const [activeHash, setActiveHash] = useState(
    () => globalThis.location?.hash.replace(/^#/, "") || "",
  );

  useEffect(() => {
    const scrollToHash = () => {
      const hash = globalThis.location?.hash;
      const id = hash ? hash.replace(/^#/, "") : "";
      setActiveHash(id);
      if (id) {
        const element = document.getElementById(id);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 50);
        }
      }
    };
    scrollToHash();
    globalThis.addEventListener("hashchange", scrollToHash);
    return () => globalThis.removeEventListener("hashchange", scrollToHash);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        gap: "32px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "clamp(10px, 3vw, 20px)",
        alignItems: "flex-start",
      }}
    >
      <nav
        style={{
          width: "240px",
          flexShrink: 0,
          position: "sticky",
          top: "20px",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          backgroundColor: "var(--surface-alt)",
          borderRadius: "var(--radius)",
          border: "1px solid var(--border)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#888",
            marginBottom: "8px",
          }}
        >
          Overview
        </div>
        <a
          href="#getting-started"
          style={navLinkStyle(activeHash === "getting-started")}
        >
          Getting Started
        </a>
        <a
          href="#n8n"
          style={navLinkStyle(activeHash === "n8n")}
        >
          n8n Integration
        </a>
        <a
          href="#make"
          style={navLinkStyle(activeHash === "make")}
        >
          Make.com (Integromat)
        </a>
        <a
          href="#zapier"
          style={navLinkStyle(activeHash === "zapier")}
        >
          Zapier Integration
        </a>
        <a
          href="#quick-start"
          style={navLinkStyle(activeHash === "quick-start")}
        >
          Node.js Quick Start
        </a>
        <a
          href="#webhook-events"
          style={navLinkStyle(activeHash === "webhook-events")}
        >
          Webhook Events
        </a>
        <a
          href="#error-handling"
          style={navLinkStyle(activeHash === "error-handling")}
        >
          Error Handling
        </a>

        <div
          style={{
            fontWeight: "bold",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#888",
            marginTop: "16px",
            marginBottom: "8px",
          }}
        >
          Endpoints
        </div>
        {endpoints.map((ep) => (
          <a
            key={ep.name}
            href={`#${ep.name}`}
            style={navLinkStyle(activeHash === ep.name)}
          >
            {ep.name}
          </a>
        ))}
      </nav>

      <div style={{ flex: 1, minWidth: 0 }}>
        <h1 style={{ marginTop: 0, marginBottom: "8px" }}>API Documentation</h1>
        <p style={{ opacity: 0.85, marginBottom: "32px" }}>
          Integrate Supergreen WhatsApp, Telegram, Facebook, and LinkedIn
          automation into your application using our HTTP API.
        </p>

        <div style={sectionStyle} id="getting-started">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Getting Started
          </h2>
          <p>
            All API endpoints require authentication using your account's secret
            token. You can find this token in your WhatsApp, Telegram, Facebook,
            or LinkedIn account settings on the main dashboard.
          </p>
          <p style={{ marginTop: "12px" }}>
            <strong>Base URL:</strong> <code>{apiBaseUrl}</code>
          </p>
          <p style={{ marginTop: "12px" }}>
            All endpoints accept JSON request bodies and return JSON responses.
            Make sure to set the <code>Content-Type: application/json</code>
            {" "}
            header.
          </p>
        </div>

        <div style={sectionStyle} id="n8n">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            n8n Automation (No-Code / Low-Code)
          </h2>
          <p>
            Automate WhatsApp and Telegram directly inside n8n without writing
            HTTP requests or managing headers. Supergreen is now verified on n8n
            Cloud:
          </p>
          <p style={{ marginTop: "12px", fontSize: "14px" }}>
            <strong>n8n Cloud:</strong> Search <code>Supergreen</code>{" "}
            directly from the node picker canvas (+) and drag it into your
            workflow.
            <br />
            <strong>Self-hosted n8n:</strong> Go to <strong>Settings</strong>
            {" "}
            &gt; <strong>Community Nodes</strong> &gt; <strong>Install</strong>
            {" "}
            and enter <code>n8n-nodes-supergreen</code>.
          </p>
          <CodeBlock code="n8n-nodes-supergreen" language="bash" />
          <p style={{ fontSize: "14px" }}>
            Supports sending text, binary media/PDFs, interactive polls,
            reactions, group management, and real-time webhook triggers for
            inbound messages.
          </p>

          <h3
            style={{
              fontSize: "1em",
              color: "#4fc3f7",
              margin: "16px 0 8px",
            }}
          >
            Ready-to-Use n8n Workflows
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "var(--text-muted)",
              marginBottom: "12px",
            }}
          >
            Copy the JSON and press <strong>Ctrl+V</strong> (or{" "}
            <strong>Cmd+V</strong>) directly on your n8n canvas to import
            instantly:
          </p>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "16px",
            }}
          >
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() => copyToClipboard(
                JSON.stringify(n8nAutoResponderWorkflow, null, 2),
              )}
            >
              <CopyIcon size={14} /> Copy Auto-Responder (Ctrl+V)
            </button>
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() =>
                downloadJson(
                  "whatsapp-ai-auto-responder.n8n.json",
                  n8nAutoResponderWorkflow,
                )}
            >
              <DownloadIcon size={14} /> Download Auto-Responder (.json)
            </button>
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() =>
                copyToClipboard(
                  JSON.stringify(n8nSendDocumentWorkflow, null, 2),
                )}
            >
              <CopyIcon size={14} /> Copy PDF Sender (Ctrl+V)
            </button>
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() =>
                downloadJson(
                  "send-whatsapp-document.n8n.json",
                  n8nSendDocumentWorkflow,
                )}
            >
              <DownloadIcon size={14} /> Download PDF Sender (.json)
            </button>
          </div>

          <p style={{ fontSize: "14px", marginTop: "8px" }}>
            <a
              href="https://github.com/uriva/n8n-nodes-supergreen"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#4fc3f7", textDecoration: "underline" }}
            >
              View GitHub Repository & Sample Workflows →
            </a>
          </p>
        </div>

        <div style={sectionStyle} id="make">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Make.com (Integromat) Integration
          </h2>
          <p>
            Automate WhatsApp and Telegram workflows in Make.com using our
            ready-to-import scenario blueprints.
          </p>

          <h3
            style={{
              fontSize: "1em",
              color: "#4fc3f7",
              margin: "16px 0 8px",
            }}
          >
            Download Ready Scenario Blueprints
          </h3>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "16px",
            }}
          >
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() =>
                downloadJson(
                  "whatsapp-ai-auto-responder.blueprint.json",
                  makeAutoResponderBlueprint,
                )}
            >
              <DownloadIcon size={14} />{" "}
              Inbound Auto-Responder Blueprint (.json)
            </button>
            <button
              type="button"
              class="button secondary"
              style={{ fontSize: "13px" }}
              onClick={() =>
                downloadJson(
                  "send-whatsapp-document.blueprint.json",
                  makeSendMediaBlueprint,
                )}
            >
              <DownloadIcon size={14} />{" "}
              Send WhatsApp Media / PDF Blueprint (.json)
            </button>
          </div>

          <h3
            style={{
              fontSize: "1em",
              color: "#4fc3f7",
              margin: "16px 0 8px",
            }}
          >
            How to Import into Make.com
          </h3>
          <ol
            style={{
              paddingLeft: "20px",
              margin: "8px 0",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "var(--text-muted)",
            }}
          >
            <li>
              Create a new scenario in Make.com and click the{" "}
              <strong>... (More)</strong> menu in the bottom toolbar.
            </li>
            <li>
              Select <strong>Import Blueprint</strong> and upload the downloaded
              {" "}
              <code>.json</code> file.
            </li>
            <li>
              In the HTTP module, update <code>token</code> and{" "}
              <code>fromNumber</code>{" "}
              with your credentials from the Supergreen dashboard.
            </li>
            <li>
              Copy the <strong>Custom Webhook</strong>{" "}
              URL from Make and add it to your Supergreen account under account
              settings or via the <code>setWebhook</code> endpoint.
            </li>
          </ol>
        </div>

        <div style={sectionStyle} id="zapier">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Zapier Integration
          </h2>
          <p>
            Connect WhatsApp and Telegram to 6,000+ apps in Zapier using the
            built-in <strong>Webhooks by Zapier</strong> app.
          </p>

          <h3
            style={{
              fontSize: "1em",
              color: "#4fc3f7",
              margin: "16px 0 8px",
            }}
          >
            1. Receive Messages in Zapier (Trigger)
          </h3>
          <ol
            style={{
              paddingLeft: "20px",
              margin: "8px 0",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "var(--text-muted)",
            }}
          >
            <li>
              In Zapier, create a Zap and choose{" "}
              <strong>Webhooks by Zapier</strong> as the trigger.
            </li>
            <li>
              Select <strong>Catch Hook</strong>{" "}
              as the event and click Continue.
            </li>
            <li>
              Copy the generated Webhook URL (e.g.{" "}
              <code>https://hooks.zapier.com/hooks/catch/...</code>).
            </li>
            <li>
              Paste this URL into your account settings in the Supergreen
              dashboard (or use the <code>setWebhook</code> endpoint).
            </li>
            <li>
              Send a test message to your connected WhatsApp/Telegram number.
              Zapier will detect the payload with sender, text, and chat ID.
            </li>
          </ol>

          <h3
            style={{
              fontSize: "1em",
              color: "#4fc3f7",
              margin: "16px 0 8px",
            }}
          >
            2. Send WhatsApp Messages from Zapier (Action)
          </h3>
          <ol
            style={{
              paddingLeft: "20px",
              margin: "8px 0",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "var(--text-muted)",
            }}
          >
            <li>
              Add an action step and select <strong>Webhooks by Zapier</strong>
              {" "}
              &gt; <strong>Custom Request</strong>.
            </li>
            <li>
              Set Method to <strong>POST</strong> and URL to{" "}
              <code>https://api.supergreen.cc/</code>.
            </li>
            <li>
              Under Headers, add <code>Content-Type: application/json</code>.
            </li>
            <li>
              In the Data field, paste the payload with your credentials and map
              the recipient number and message:
            </li>
          </ol>
          <CodeBlock
            code={`{
  "endpoint": "sendMessage",
  "payload": {
    "token": "YOUR_SUPERGREEN_TOKEN",
    "fromNumber": "YOUR_PHONE",
    "toNumber": "15551234567@c.us",
    "message": "Hello from Zapier!"
  }
}`}
          />
        </div>

        <div style={sectionStyle} id="quick-start">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Node.js Quick Start
          </h2>
          <CodeBlock
            language="javascript"
            code={`// Install the client (optional)
// npm install node-fetch

const fetch = require('node-fetch');

const API_BASE = '${apiBaseUrl}';
const YOUR_TOKEN = 'your-secret-token-here';
const YOUR_PHONE = 'your-whatsapp-number';

async function sendMessage(toNumber, message) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: 'sendMessage',
      payload: {
        fromNumber: YOUR_PHONE,
        toNumber: toNumber,
        message: message,
        linkPreview: false,
        token: YOUR_TOKEN
      }
    })
  });
  return response.json();
}

// Example usage
sendMessage('15551234567@c.us', 'Hello from Node.js!')
  .then(result => console.log('Message sent:', result))
  .catch(err => console.error('Error:', err));`}
          />
        </div>

        <div style={sectionStyle} id="webhook-events">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Webhook Events</h2>
          <p>
            When you configure a webhook URL, Supergreen will send POST requests
            to your server whenever WhatsApp or Telegram messages are received,
            or when Facebook group posts are scraped. The <code>fromMe</code>
            {" "}
            field (boolean) indicates whether the message was sent from the
            connected line itself (outgoing) or from another participant
            (incoming). The webhook payload follows this structure:
          </p>
          <CodeBlock
            code={JSON.stringify(
              {
                network: "telegram",
                chat: {
                  id: "-1001234567890",
                  username: "example_channel",
                  title: "Example Group",
                },
                author: {
                  id: "123456789",
                  username: "johndoe",
                },
                text: "Hello! This is a message from a user.",
                time: 1703251200000,
                fromMe: false,
              },
              null,
              2,
            )}
          />
        </div>

        <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>Endpoints</h2>

        {endpoints.map((ep, i) => <EndpointSection key={i} {...ep} />)}

        <div style={sectionStyle} id="error-handling">
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Error Handling</h2>
          <p>
            All API endpoints return standard HTTP status codes. Successful
            requests return 200 OK. Errors return appropriate status codes with
            JSON error details:
          </p>
          <CodeBlock
            code={JSON.stringify(
              {
                error: "Authentication failed",
                message: "Invalid token provided",
              },
              null,
              2,
            )}
          />
          <p style={{ marginTop: "12px" }}>Common error codes:</p>
          <ul style={{ marginLeft: "20px", opacity: 0.9 }}>
            <li>400 - Bad Request (invalid parameters)</li>
            <li>401 - Unauthorized (invalid token)</li>
            <li>404 - Not Found (endpoint doesn't exist)</li>
            <li>500 - Internal Server Error</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
